import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { commentModels, fileEnums, FindLoad, KeyPurpose, noteModels, PermissionList, RolePermission, User } from 'utils';
import { SimpleTabs, Loader, Notes, SimpleModal, FilesComponent, Comments } from 'components';
import {
    StaffGeneral,
    StaffCredentials,
    StaffEmployment,
    StaffAccess,
    StaffItemHeader,
    StaffTimesheet,
    PastStaffs,
    StaffAvailability,
    StaffService,
    staffTabsLabels,
    StaffAssignment,
    StaffSettings,
    StaffHistory,
} from './core';
import { CreateStaff } from '../createStaff';
import { StaffDetails } from '../createStaff';
import { httpRequestsOnErrorsActions } from 'store';
import { formModelEnums } from '../../../pages';

export const StaffItem = ({}) => {
    const { staffGeneral, employments, staffServices, serviceTypes, rolesList, accessList } = useSelector((state) => ({
        staffGeneral: state.admins.adminInfoById,
        staffCredentialList: state.admins.credential,
        employments: state.admins.employments,
        staffServices: state.admins.staffServices,
        serviceTypes: state.system.services,
        rolesList: state.roles.rolesList?.roles,
        accessList: state.auth.accessList,
    }));
    const dispatch = useDispatch();
    const params = useParams();
    const [staffGeneralModal, setStaffGeneralModal] = useState('');
    const loaderItems = FindLoad('GET_ADMIN_BY_ID');
    const { userInfo } = User();
    const checkTimesheet = RolePermission([PermissionList.STAFF_TIMESHEET_READ?.code])
        ? true
        : userInfo?.id === (staffGeneral?.id || params?.id)
        ? RolePermission([PermissionList.SELF_TIMESHEET_READ?.code])
        : false;
    const checkMySettings = userInfo?.id === (staffGeneral?.id || params?.id);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const info = location?.state;

    function handleClose() {
        setStaffGeneralModal('');
    }

    const tabsContent = [
        RolePermission([PermissionList.STAFF_READ.code, PermissionList.STAFF_CREATE.code, PermissionList.STAFF_STATUS?.code]) && {
            tabComponent: !!loaderItems.length ? (
                <Loader />
            ) : (
                <StaffGeneral
                    handleOpenModal={(type) => setStaffGeneralModal(type)}
                    staffGeneral={staffGeneral}
                    pastEmployments={employments}
                />
            ),
            tab: 'General',
        },
        RolePermission([PermissionList.STAFF_PAYCODE_MANAGE?.code, PermissionList.STAFF_PAYCODE_READ?.code]) && {
            tabComponent: <StaffEmployment />,
            tab: 'Paycodes',
        },
        checkTimesheet && {
            tabComponent: <StaffTimesheet />,
            tab: 'Timesheet',
        },
        RolePermission([PermissionList.STAFF_CREDENTIAL_MANAGE?.code, PermissionList.STAFF_CREDENTIAL_READ?.code]) && {
            tabComponent: <StaffCredentials />,
            tab: 'Credentials',
        },
        RolePermission([PermissionList.STAFF_READ.code]) && {
            tabComponent: <StaffAvailability />,
            tab: 'Availability',
        },
        RolePermission([PermissionList.STAFF_ROLES_MANAGE?.code]) && {
            tabComponent: <StaffAccess rolesList={rolesList} accessList={accessList} />,
            tab: 'Access',
        },
        RolePermission([PermissionList.CLIENT_STAFF_ASSIGNMENT_READ?.code, PermissionList.CLIENT_STAFF_ASSIGNMENT?.code]) && {
            tabComponent: <StaffAssignment />,
            tab: 'Assigned Clients',
        },
        RolePermission([PermissionList.STAFF_SERVICE_MANAGE?.code, PermissionList.STAFF_SERVICE_READ?.code]) && {
            tabComponent: <StaffService services={serviceTypes?.serviceTypes} info={staffServices} staffGeneral={staffGeneral} />,
            tab: 'Blocked Services',
        },

        checkMySettings && {
            tabComponent: <StaffSettings staffGeneral={staffGeneral} />,
            tab: 'My Settings',
        },
        {
            tabComponent: (
                <Comments
                    fromDetail={true}
                    resource={commentModels.Client}
                    resourceId={params?.id}
                    label={'Client'}
                    width={700}
                    height={600}
                    attachmentPurpose={KeyPurpose.CLINICAL_DOC}
                    enableAiImprove
                />
            ),
            tab: 'Comments',
        },
        RolePermission([PermissionList.STAFF_NOTE_MANAGE?.code, PermissionList.STAFF_NOTE_READ?.code]) && {
            tabComponent: (
                <Notes
                    id={staffGeneral?.id}
                    onModel={noteModels.Staff}
                    formModel={formModelEnums.STAFFNOTES}
                    permissions={[PermissionList.STAFF_NOTE_MANAGE?.code]}
                />
            ),
            tab: 'Notes',
        },
        RolePermission([PermissionList.STAFF_HISTORY_READ?.code]) && {
            tabComponent: <StaffHistory />,
            tab: 'History',
        },
        RolePermission([PermissionList.STAFF_FILE_MANAGE?.code, PermissionList.STAFF_FILE_READ?.code]) && {
            tabComponent: <FilesComponent onResource={fileEnums.STAFF} permissions={[PermissionList.STAFF_FILE_MANAGE?.code]} />,
            tab: 'Files',
        },
    ];

    const navigateTab = () => {
        const params = {
            ...info,
        };
        navigate(`/staff/${staffGeneral?.id}`, {
            state: {
                ...params,
                activeTab: 'My Settings',
            },
        });
    };

    useEffect(() => {
        const activeTab = searchParams.get('activeTab');
        if (activeTab && staffGeneral?.id) {
            navigate(`/staff/${staffGeneral?.id}`, {
                state: {
                    ...location.state,
                    activeTab,
                },
            });
        }
    }, [staffGeneral?.id, location.search]);

    useEffect(() => {
        if (location?.pathname === `/staff/${staffGeneral?.id}/success` && info?.activeTab !== 'My Settings') {
            navigateTab();
        }
        if (location?.pathname === `/staff/${staffGeneral?.id}/forbidden` && info?.activeTab !== 'My Settings') {
            dispatch(httpRequestsOnErrorsActions.appendError('CALENDAR_INTEGRATION_FORBIDDEN'));
            navigateTab();
        }
        if (location?.pathname === `/staff/${staffGeneral?.id}/wrong` && info?.activeTab !== 'My Settings') {
            dispatch(httpRequestsOnErrorsActions.appendError('CALENDAR_WRONG_INTEGRATION'));
            navigateTab();
        }
    }, [info]);

    return (
        <>
            <div className="tab-box-wrapper">
                {RolePermission([PermissionList.STAFF_READ.code]) && <StaffItemHeader />}
                <SimpleTabs
                    defaultTab={
                        location?.pathname === `/staff/${staffGeneral?.id}/forbidden`
                            ? 'My Settings'
                            : location?.pathname === `/staff/${staffGeneral?.id}/success`
                            ? 'My Settings'
                            : location?.pathname === `/staff/${staffGeneral?.id}/wrong`
                            ? 'My Settings'
                            : info?.state?.activeTab === 'My Settings'
                            ? 'My Settings'
                            : info?.state?.activeTab === 'Assigned Clients'
                            ? 'Assigned Clients'
                            : info?.activeTab === 'My Settings'
                            ? 'My Settings'
                            : RolePermission([
                                  PermissionList.STAFF_READ?.code,
                                  PermissionList.STAFF_CREATE?.code,
                                  PermissionList.STAFF_STATUS?.code,
                              ])
                            ? 'General'
                            : !RolePermission([PermissionList.STAFF_READ.code]) && checkMySettings
                            ? 'My Settings'
                            : ''
                    }
                    tabsLabels={staffTabsLabels(checkTimesheet, checkMySettings)}
                    tabsContent={tabsContent}
                />
            </div>

            <SimpleModal
                openDefault={!!staffGeneralModal}
                handleOpenClose={() => setStaffGeneralModal('')}
                content={
                    staffGeneralModal === 'general' ? (
                        <CreateStaff staffGeneral={staffGeneral} handleClose={handleClose} />
                    ) : staffGeneralModal === 'other' ? (
                        <StaffDetails staffGeneral={staffGeneral} handleClose={handleClose} />
                    ) : // staffGeneralModal === 'pastEmployment' ? (
                    //         <PastStaffs
                    //             employments={employments}
                    //         />
                    //     ) :
                    // (
                    // <EmploymentModal
                    //     info={staffGeneralModal === 'employment' ? activeEmployment : ''}
                    //     handleClose={handleClose}
                    //     active={activeEmployment}
                    // />
                    // )
                    null
                }
            />
        </>
    );
};
